import React, { useContext, useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { getLocations } from "../../api/services/scheduling";
import useLocation from "../../hooks/useLocation";
import MyContext from "../../context/MyContext";
import { useQuery } from "react-query";
import { QK } from "../../base/qk";
import { useAuth } from "../../context/AuthContext";

import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Select,
  Box,
  IconButton,
  Avatar,
  Divider,
  alpha,
  useTheme,
  Badge,
  Fade,
} from "@mui/material";
import {
  LocationOn,
  ExitToApp,
  KeyboardArrowDown,
  Dashboard,
  Notifications,
} from "@mui/icons-material";
import NotificationPopover from "./NotificationPopover";
import { useUserProfile } from "../../hooks/userProfile";

function Header() {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const userInfo = useUserProfile();
  const theme = useTheme();

  const locationIdFromHook = useLocation();
  const { setLocationID } = useContext(MyContext);

  const [selectedLocationId, setSelectedLocationId] = useState(() => {
    return localStorage.getItem("location_id") || "all";
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);

  useEffect(() => {
    const savedLocationId = localStorage.getItem("location_id");

    if (savedLocationId) {
      setSelectedLocationId(savedLocationId);
      setLocationID(savedLocationId);
    } else if (locationIdFromHook) {
      setSelectedLocationId(locationIdFromHook);
      setLocationID(locationIdFromHook);
      localStorage.setItem("location_id", locationIdFromHook);
    }
  }, [locationIdFromHook, setLocationID]);

  useEffect(() => {
    const handler = (e) => setHasUnread(!!e.detail?.hasUnread);
    window.addEventListener("notification-unread", handler);
    return () => window.removeEventListener("notification-unread", handler);
  }, []);

  const { data: locations } = useQuery([QK.location], () => getLocations(), {
    enabled: !!userInfo,
  });

  const handleLocationSelect = (e) => {
    const locId = e.target.value;
    localStorage.setItem("location_id", locId);
    setLocationID(locId);
    setSelectedLocationId(locId);
  };

  const logoutHandler = () => {
    setAnchorEl(null);
    localStorage.removeItem("location_id");
    logout();
    navigate("/login");
  };

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleNotificationOpen = (e) => setNotificationAnchor(e.currentTarget);
  const handleNotificationClose = () => setNotificationAnchor(null);

  const enhancedLocations = [
    { id: "all", name: "All Zones" },
    ...(locations || []),
  ];

  const isUserLoggedIn = isAuthenticated || userInfo;

  const fullName =
    [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" ") ||
    userInfo?.username ||
    "User";

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        zIndex: 1000,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
        boxShadow: `0 2px 12px ${alpha(theme.palette.primary.dark, 0.25)}`,
      }}
    >
      <style>
        {`
          @keyframes bellBlink {
            0%, 100% {
              transform: rotate(0deg) scale(1);
              color: #FFFFFF;
              filter: drop-shadow(0 0 0px transparent);
            }
            15% {
              transform: rotate(-14deg) scale(1.12);
              color: #FFD54F;
              filter: drop-shadow(0 0 8px rgba(255, 213, 79, 0.9));
            }
            30% {
              transform: rotate(12deg) scale(1.12);
              color: #FFD54F;
              filter: drop-shadow(0 0 8px rgba(255, 213, 79, 0.9));
            }
            45% {
              transform: rotate(-10deg) scale(1.08);
              color: #FFD54F;
              filter: drop-shadow(0 0 6px rgba(255, 213, 79, 0.7));
            }
            60% {
              transform: rotate(8deg) scale(1.08);
              color: #FFD54F;
              filter: drop-shadow(0 0 6px rgba(255, 213, 79, 0.7));
            }
            75% {
              transform: rotate(-4deg) scale(1.04);
            }
            100% {
              transform: rotate(0deg) scale(1);
            }
          }
          @keyframes dotPulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.9);
            }
            50% {
              transform: scale(1.25);
              box-shadow: 0 0 0 6px rgba(244, 67, 54, 0);
            }
          }
          .notification-blink {
            animation: bellBlink 1.6s ease-in-out infinite;
          }
          .notification-dot {
            animation: dotPulse 1.4s ease-in-out infinite;
          }
        `}
      </style>

      <Toolbar
        sx={{
          justifyContent: "space-between",
          minHeight: { xs: 56, sm: 64 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography
            variant="h6"
            component={Link}
            to={isUserLoggedIn ? "/" : "/login"}
            sx={{
              color: "inherit",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: theme.palette.secondary.main,
                boxShadow: `0 0 10px ${alpha(
                  theme.palette.secondary.main,
                  0.6
                )}`,
              }}
            />
            AllForCare
          </Typography>

          {isUserLoggedIn && (
            <Fade in timeout={800}>
              <Select
                size="small"
                value={selectedLocationId}
                onChange={handleLocationSelect}
                IconComponent={KeyboardArrowDown}
                sx={{
                  backgroundColor: alpha(theme.palette.common.white, 0.15),
                  borderRadius: 2,
                  minWidth: 160,
                  fontSize: 14,
                  color: "inherit",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.common.white, 0.25),
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSelect-select": {
                    paddingY: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  },
                  "& .MuiSvgIcon-root": {
                    color: "inherit",
                  },
                }}
                renderValue={(value) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LocationOn sx={{ fontSize: 18 }} />
                    {enhancedLocations.find(
                      (loc) => String(loc.id) === String(value)
                    )?.name || "Select Zone"}
                  </Box>
                )}
              >
                {enhancedLocations.map((loc) => (
                  <MenuItem
                    key={loc.id}
                    value={loc.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minHeight: "auto",
                      py: 1,
                    }}
                  >
                    {loc.id === "all" ? (
                      <Dashboard sx={{ fontSize: 18, opacity: 0.8 }} />
                    ) : (
                      <LocationOn sx={{ fontSize: 18, opacity: 0.8 }} />
                    )}
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </Fade>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isUserLoggedIn ? (
            <>
              <IconButton
                onClick={handleNotificationOpen}
                color="inherit"
                sx={{
                  p: 0.8,
                  background: alpha(theme.palette.common.white, 0.12),
                  "&:hover": {
                    background: alpha(theme.palette.common.white, 0.22),
                  },
                }}
              >
                {hasUnread ? (
                  <Badge
                    variant="dot"
                    overlap="circular"
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{
                      "& .MuiBadge-dot": {
                        backgroundColor: "#F44336",
                        boxShadow: "0 0 6px rgba(244, 67, 54, 0.9)",
                      },
                    }}
                    classes={{ badge: "notification-dot" }}
                  >
                    <Notifications
                      className="notification-blink"
                      sx={{ fontSize: 22 }}
                    />
                  </Badge>
                ) : (
                  <Notifications sx={{ fontSize: 22 }} />
                )}
              </IconButton>

              <NotificationPopover
                open={Boolean(notificationAnchor)}
                anchorEl={notificationAnchor}
                onOpen={handleNotificationOpen}
                onClose={handleNotificationClose}
                onUnreadChange={setHasUnread}
              />

              <IconButton
                onClick={handleMenuOpen}
                color="inherit"
                sx={{
                  p: 0.8,
                  background: alpha(theme.palette.common.white, 0.12),
                  "&:hover": {
                    background: alpha(theme.palette.common.white, 0.22),
                  },
                }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  sx={{
                    "& .MuiBadge-dot": {
                      backgroundColor: theme.palette.success.main,
                      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
                    },
                  }}
                >
                  <Avatar
                    src={
                      userInfo?.profile_photo
                        ? userInfo.profile_photo
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            fullName
                          )}&background=random`
                    }
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "0.9rem",
                      fontWeight: 600,
                    }}
                  >
                    {fullName.charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  elevation: 4,
                  sx: {
                    borderRadius: 3,
                    mt: 1.5,
                    minWidth: 220,
                    overflow: "visible",
                    "&:before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {fullName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {userInfo?.email || ""}
                  </Typography>
                  {userInfo?.user_type && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "inline-block",
                        mt: 0.5,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        textTransform: "capitalize",
                        fontWeight: 600,
                      }}
                    >
                      {userInfo.user_type}
                    </Typography>
                  )}
                </Box>
                <Divider />
                <MenuItem
                  onClick={logoutHandler}
                  sx={{
                    py: 1.5,
                    gap: 1.5,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <ExitToApp sx={{ fontSize: 20, color: "text.secondary" }} />
                  <Typography variant="body2">Logout</Typography>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Button
                component={NavLink}
                to="/login"
                variant="outlined"
                color="inherit"
                size="small"
                sx={{
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.common.white, 0.4),
                  color: "inherit",
                  "&:hover": {
                    borderColor: theme.palette.common.white,
                    backgroundColor: alpha(theme.palette.common.white, 0.1),
                  },
                }}
              >
                Login
              </Button>
              <Button
                component={NavLink}
                to="/register"
                variant="contained"
                color="secondary"
                size="small"
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: `0 4px 12px ${alpha(
                    theme.palette.secondary.main,
                    0.4
                  )}`,
                  "&:hover": {
                    boxShadow: `0 6px 16px ${alpha(
                      theme.palette.secondary.main,
                      0.5
                    )}`,
                  },
                }}
              >
                Register
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Header;